import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';

import type { DatabaseEntities, Maybe, YardTaskAttachment } from '@nova/core';
import { documentKeys } from '@satellite/composables/queryKeys';
import documentService from '@satellite/services/document-service';

type UploadAttachmentVariables = {
  relatedEntity: DatabaseEntities;
  relatedId: string;
  file: File;
  /** Silences the AxiosPlugin interceptor, for a caller that reports the failure itself. */
  suppressNotification?: boolean;
};

export interface UseAttachmentUploadOptions {
  /** Which entity the documents hang off, e.g. `DatabaseEntities.YardTask`. */
  relatedEntity: MaybeRefOrGetter<DatabaseEntities>;
  /** The entity's id. Nothing is uploaded until there is one. */
  relatedId: MaybeRefOrGetter<Maybe<string>>;
  /** Runs once the upload succeeds, before the list is invalidated. */
  onSuccess?: (attachment: YardTaskAttachment) => void;
  /** Runs when the upload fails. `uploadAttachment` still resolves. */
  onError?: (error: unknown) => void;
}

/**
 * Adds a document to an entity's attachments and invalidates the list it belongs to, so every
 * page comes back from the API rather than being patched locally. Accepts any file: which of
 * them an app is willing to attach is `getAttachmentRejection`'s call, before this is reached.
 * Pairs with `useAttachmentList` and `useAttachmentDelete`.
 */
export function useAttachmentUpload({
  relatedEntity,
  relatedId,
  onSuccess,
  onError
}: UseAttachmentUploadOptions) {
  const queryClient = useQueryClient();

  const mutation = useMutation<YardTaskAttachment, unknown, UploadAttachmentVariables>({
    // The request config is passed only when silencing, so the ordinary upload reaches the
    // service exactly as it did before there was a staged one.
    mutationFn: ({ relatedEntity: entity, relatedId: id, file, suppressNotification }) =>
      suppressNotification
        ? documentService.uploadDocument(entity, id, file, {
            requestConfig: { suppressNotification: true }
          })
        : documentService.uploadDocument(entity, id, file),
    onSuccess: async (attachment, variables) => {
      onSuccess?.(attachment);

      await queryClient.invalidateQueries({
        queryKey: documentKeys.list(variables.relatedEntity, variables.relatedId)
      });
    },
    onError
  });

  /**
   * Never rejects: failures reach the caller through `onError`.
   *
   * Both the entity and its id are read here rather than on the way back, so a list that moved
   * on while the request was in flight is not the one invalidated.
   */
  async function uploadAttachment(file: Maybe<File>): Promise<void> {
    const currentEntity = toValue(relatedEntity);
    const currentId = toValue(relatedId);
    if (!currentId || !file) {
      return;
    }

    try {
      await mutation.mutateAsync({ relatedEntity: currentEntity, relatedId: currentId, file });
    } catch {
      // Reported by onError.
    }
  }

  /**
   * Uploads files that were held client-side while the entity did not exist yet, against the id
   * it was given once created. `entityId` is an argument rather than the reactive `relatedId`,
   * which is still empty at the moment a create form has something to attach to.
   *
   * Sequential on purpose: a form carries a handful of files, and one failure should not be
   * buried under five more in flight. Never rejects — what failed comes back for the caller to
   * report, so a created entity is not made to look like a failed one.
   *
   * Pairs with `useStagedAttachments`, which holds the files until this is called.
   */
  async function uploadStagedAttachments(
    entityId: string,
    files: File[]
  ): Promise<Array<{ file: File; error: unknown }>> {
    const currentEntity = toValue(relatedEntity);
    const failures: Array<{ file: File; error: unknown }> = [];

    for (const file of files) {
      try {
        // Sequential on purpose: `Promise.all` here is what buries one failure under the rest.
        // eslint-disable-next-line no-await-in-loop
        await mutation.mutateAsync({
          relatedEntity: currentEntity,
          relatedId: entityId,
          file,
          // The caller reports these together, by count; the interceptor would add one
          // generic toast per file on top of that.
          suppressNotification: true
        });
      } catch (error) {
        failures.push({ file, error });
      }
    }

    return failures;
  }

  return {
    uploadAttachment,
    uploadStagedAttachments,
    isUploading: computed(() => mutation.isPending.value)
  };
}
